import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

const SITE_URL = "https://softlinetechs.com.ng";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Per-route SEO: title, description, canonical + OG/Twitter.
 * Static fallbacks for the homepage live in public/index.html.
 */
export function Seo({ title, description, path = "/", type = "website", jsonLd = null }) {
  const url = `${SITE_URL}${path === "/" ? "" : path}`;
  useEffect(() => {
    if (title) document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", type);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", url);
  }, [title, description, url, type]);

  if (jsonLd) {
    return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />;
  }
  return null;
}

export function HelmetProviderWrapper({ children }) {
  return <>{children}</>;
}

/** Masked line-by-line headline reveal. */
export function MaskedLines({ lines, className = "", lineClassName = "", delay = 0, as: Tag = "h1" }) {
  return (
    <Tag className={className}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
          <motion.span
            className={`block will-change-transform ${lineClassName}`}
            initial={{ y: "112%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 0.9, delay: delay + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
}

/** Scroll-into-view reveal wrapper. */
export function Reveal({ children, delay = 0, y = 28, className = "", once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.15 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.div>
  );
}

/** Subtle scroll parallax for imagery. */
export function ParallaxImage({ src, alt, className = "", imgClassName = "", distance = 48 }) {
  const ref = useRef(null);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        className={`absolute inset-0 h-full w-full object-cover scale-[1.15] ${imgClassName}`}
        initial={{ y: -distance }}
        whileInView={{ y: distance }}
        viewport={{ once: false, amount: 0.1 }}
        transition={{ ease: "linear" }}
      />
    </div>
  );
}

/** Smooth-scroll to an in-page anchor, Lenis-aware. */
export function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) {
    window.__lenis.scrollTo(el, { offset: -90 });
  } else {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}
